import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { first } from 'rxjs/operators';

import { AppGlobals } from '@app/app.global';
import { AuthService, SharedService, User } from '@app/core';
import { UniqueEmailValidator, UniqueMobileValidator } from '@modules/auth/validators';

@Component({
  selector: 'app-signup', // The selector used to identify the component in HTML
  templateUrl: './signup.component.html', // The URL of the component's template file
  styleUrls: ['./signup.component.scss'], // The URLs of the component's style files
  host: {
    style: "display:block;", // Inline style applied to the host element
    class: "loginwrapper", // CSS class added to the host element
    "[attr.tabindex]": "-1", // Binding of the "tabindex" attribute to the value -1
  }
})
export class SignupComponent implements OnInit {
  step: number = 1;
  token: string = '';
  user: User;

  @ViewChild('passwordInput') passwordInput: ElementRef;
  errorMessageSignUp: string = '';
  successMessageSignUp: string = '';
  signUpForm: FormGroup;
  isSubmitAttempted: boolean = false;
  isPasswordVisible: boolean = false;

  verifyOtpForm: FormGroup;
  time: string = '00:60';
  isSubmitAttemptedVerify: boolean = false;
  // Declare ViewChild references for the input fields
  @ViewChild('count1Input') count1Input: ElementRef;
  @ViewChild('count2Input') count2Input: ElementRef;
  @ViewChild('count3Input') count3Input: ElementRef;
  @ViewChild('count4Input') count4Input: ElementRef;
  @ViewChild('count5Input') count5Input: ElementRef;
  @ViewChild('count6Input') count6Input: ElementRef;
  count1IsEmpty: boolean = true;
  count2IsEmpty: boolean = true;
  count3IsEmpty: boolean = true;
  count4IsEmpty: boolean = true;
  count5IsEmpty: boolean = true;
  count6IsEmpty: boolean = true;
  allFieldsFilled: boolean = false;
  errorMessageVerify: any = false;
  successMessageVerify: any = false;
  private timerInterval: any;
  isVerifying: boolean = false;

  companyDetailsForm: FormGroup;
  errorMsgCmpInfoAdd: string = '';
  typeOfBussinessData: any[];
  successMsgCmpInfoAdd: string = '';
  productPossibleHeads: { name: string, field: string }[] = [];
  businessType = false;
  disableResendButton: boolean = false;
  disableButtonOnError: boolean = false;
  isOtherSelected: boolean = false;
  errorMessage: string;

  constructor(public router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    public appGlobals: AppGlobals,
    public sharedService: SharedService,
    private authService: AuthService,
    private uniqueEmailValidator: UniqueEmailValidator,
    private uniqueMobileValidator: UniqueMobileValidator) {
    // redirect to home if already logged in and onboarded
    if (this.authService.isLoggedIn) {
      if (this.authService.user.isOnboarded) {
        this.router.navigate(this.appGlobals.homePath);
      } else {
        this.user = this.authService.user;
        this.token = this.authService.token;
      }
    }
  }

  ngOnInit(): void {
    if (this.route.snapshot.params['token'] && this.route.snapshot.params['user'] && this.route.snapshot.params['otp']) {
      this.createVerifyOtpForm();
      this.user = new User(JSON.parse(this.sharedService.decrypt(this.route.snapshot.params['user'])));
      this.token = this.route.snapshot.params['token'];
      const otp = this.route.snapshot.params['otp'].split('');
      this.step = 2;
      this.verifyOtpForm.setValue({
        count1: otp?.[0],
        count2: otp?.[1],
        count3: otp?.[2],
        count4: otp?.[3],
        count5: otp?.[4],
        count6: otp?.[5]
      });
      this.verifyOtp();
    } else if (this.user && !this.user.isEmailVerified && !this.user.isMobileVerified) {
      this.createVerifyOtpForm();
      this.step = 2;
      this.startTimer();
    } else if (this.user && !this.user.isOnboarded) {
      this.step = 3;
      this.createCompanyDetailsForm();
      this.fetchtypeOfBusiness(this.token);
    }
    this.createSignupForm();

    if (localStorage.getItem('step') && !this.route.snapshot.params['token'] && !this.route.snapshot.params['user'] && !this.route.snapshot.params['otp']) {
      this.step = parseInt(localStorage.getItem('step'));
      const user = JSON.parse(this.sharedService.decrypt(localStorage.getItem('temporaryUser')));

      this.user = new User(user.props);
      this.token = localStorage.getItem('temporaryToken');

      if (this.step == 2) {
        this.createVerifyOtpForm()
      } else {
        this.createCompanyDetailsForm();
        this.fetchtypeOfBusiness(this.token);
      }
    }
  }

  get f() { return this.signUpForm.controls; }
  get verifyf() { return this.verifyOtpForm.controls; }
  get companyf() { return this.companyDetailsForm.controls }

  /**
  * Create the signUpForm FormGroup and set up value change subscriptions for email and mobile form controls
  * @author Darshan Malani 10:45 AM 6/30/2023
  * @param
  */
  createSignupForm() {
    this.signUpForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.pattern(this.appGlobals.regex.fullname)]],
      email: ['', [Validators.required, Validators.pattern(this.appGlobals.regex.email)], [this.uniqueEmailValidator.validate.bind(this.uniqueEmailValidator)]],
      mobile: ['', [Validators.required, Validators.pattern(this.appGlobals.regex.mobile)], [this.uniqueMobileValidator.validate.bind(this.uniqueMobileValidator)]],
      password: ['', [Validators.required, Validators.minLength(this.appGlobals.minPasswordLength), Validators.pattern(this.appGlobals.regex.password)]],
    });
  }

  /** Create the verifyForm FormGroup and set up validation rules for count1 to count6 form controls.
  * @author Darshan Malani 10:45 AM 6/30/2023
  * @param 
  */
  createVerifyOtpForm() {
    this.verifyOtpForm = this.formBuilder.group({
      count1: ['', [Validators.required, this.onlyNumericValidator]],
      count2: ['', [Validators.required, this.onlyNumericValidator]],
      count3: ['', [Validators.required, this.onlyNumericValidator]],
      count4: ['', [Validators.required, this.onlyNumericValidator]],
      count5: ['', [Validators.required, this.onlyNumericValidator]],
      count6: ['', [Validators.required, this.onlyNumericValidator]]
    });
  }

  /**
   * Create the companyDetailsForm FormGroup and set up validation rules for PAN, GST, name, domain, other_domain, and turn_over form controls.
    The PAN control is validated using a regular expression pattern for PAN numbers.
    The GST control is validated using a regular expression pattern for GST numbers.
    The name, domain, other_domain, and turn_over controls have no specific validation rules.
    The form group also includes a custom validator called 'isPANorGSTSelected' to validate if either PAN or GST is selected.
    This function is responsible for initializing the companyDetailsForm FormGroup.
  * @author Darshan Malani 10:45 AM 6/30/2023
  * @param 
  */
  createCompanyDetailsForm() {
    this.companyDetailsForm = this.formBuilder.group({
      gst: ['', [Validators.pattern(this.appGlobals.regex.gst)]],
      pan: ['', [Validators.required, Validators.pattern(this.appGlobals.regex.pan)]],
      name: ['', [Validators.required, Validators.maxLength(128), Validators.pattern(this.appGlobals.regex.companyName)]],
      domain: [null, []],
      otherDomain: ['', []]
    }, { validator: this.isPANorGSTSelected });
  }

  /**
  * The signupSubmit function is responsible for handling the submission of the signup form in your application.
  * @author Darshan Malani 10:45 AM 6/30/2023
  * @param 
  */
  signupSubmit() {
    this.isSubmitAttempted = true; // Set the flag to indicate that a signup attempt has been made

    if (this.signUpForm.invalid) {
      this.errorMessageSignUp = 'Please enter valid credentials.'; // Display an error message if the form is invalid
    } else {
      // If the form is valid, call the signup method of the authService to submit the signup data
      this.authService.signup(this.signUpForm.value).pipe(first()).subscribe(
        (res: any) => {
          this.token = res.token;
          this.user = res.user;
          this.successMessageSignUp = res.message; // Handle the success response from the backend
          this.errorMessageSignUp = '';
          this.step = 2;
          localStorage.setItem('step', `${this.step}`);
          localStorage.setItem('temporaryToken', `${this.token}`);
          localStorage.setItem('temporaryUser', this.sharedService.encrypt(JSON.stringify(this.user)));
          this.startTimer();
          this.createVerifyOtpForm();
        },
        (error: any) => {
          this.errorMessageSignUp = error; // Handle the error response from the backend
          this.successMessageSignUp = '';
        }
      );
    }
  }

  /**
  * The signupSubmit function is responsible for handling the submission of the verifyCodeSubmit form in our application.
  * @author Darshan Malani 10:45 AM 6/30/2023
  * @param 
  */
  verifyOtp() {
    if (this.isVerifying) {
      return; // Exit the function if verification is already in progress
    }
    // this.startTimer();
    const otp = this.verifyOtpForm.value.count1 +
      this.verifyOtpForm.value.count2 +
      this.verifyOtpForm.value.count3 +
      this.verifyOtpForm.value.count4 +
      this.verifyOtpForm.value.count5 +
      this.verifyOtpForm.value.count6;

    this.isSubmitAttempted = true;
    this.isVerifying = true; // Set isVerifying to true before making the API call
    this.authService.verifyOtp(otp, this.token).pipe(first()).subscribe(
      (res: any) => {
        if (res.user?.isOnboarded) {
          this.authService.logout();
        } else {
          this.token = res.token;
          this.user = res.user;
          this.step = 3;
          localStorage.setItem('step', `${this.step}`);
          this.isVerifying = false;
          this.isSubmitAttempted = false;
          this.createCompanyDetailsForm();
          this.fetchtypeOfBusiness(this.token);
          this.stopTimer();
        }
      },
      (error: any) => {
        this.errorMessageVerify = error;
        this.disableButtonOnError = true;
        this.isVerifying = false; // Set isVerifying to false if there is an error in the API call
        // this.stopTimer();
        this.verifyOtpForm.setValue({
          count1: '',
          count2: '',
          count3: '',
          count4: '',
          count5: '',
          count6: ''
        });
      }
    );
  }

  /**
  * The reSendCodeSubmit function is responsible for resending the verification code in our application.
  * @author Darshan Malani 10:45 AM 6/30/2023
  * @param 
  */
  reSendCodeSubmit(): void {
    this.startTimer();
    this.disableResendButton = true; // Disable the link
    if (this.user.mobile) {
      this.authService.reSendOtp(this.token).pipe(first()).subscribe(
        (res: any) => {
          if (res.user?.isOnboarded) {
            this.authService.logout();
          } else {
            this.token = res.token;
            this.user = res.user;
            this.successMessageVerify = res.message;
            this.errorMessageVerify = false;
          }
        },
        (error: any) => {
          this.errorMessageVerify = error;
          this.successMessageVerify = false;
        }
      );
    }
  }
  /**
* The companyDetailsSubmit function is responsible for send the companyDetailsSubmit code in our application.
* @author Darshan Malani 10:45 AM 6/30/2023
* @param 
*/
  companyDetailsSubmit() {
    if (this.companyDetailsForm.invalid) {
      this.errorMessageSignUp = 'Please enter mendatory fields.'; // Display an error message if the form is invalid
    } else {
      const company = {
        pan: this.companyDetailsForm.value.pan,
        gst: this.companyDetailsForm.value.gst,
        name: this.companyDetailsForm.value.name
      };

      if (this.companyDetailsForm.value.domain) {
        company['domain'] = this.companyDetailsForm.value.domain;
      }

      if (this.companyDetailsForm.value.otherDomain) {
        company['otherDomain'] = this.companyDetailsForm.value.otherDomain;
      }

      this.authService.addCompanyDetails(this.token, company).pipe(first()).subscribe(
        (res: any) => {
          this.successMsgCmpInfoAdd = res.message;
          this.errorMsgCmpInfoAdd = '';
          this.sharedService.setSuccessMessage('Thank you! You have successfully registered. You can start your journey by signing in.');
          this.authService.logout();
        },
        (error: any) => {
          this.successMsgCmpInfoAdd = '';
          this.errorMsgCmpInfoAdd = error;
        }
      );
    }
  }

  isPANorGSTSelected(control: AbstractControl): ValidationErrors | null {
    const panControl = control.get('pan');
    const gstControl = control.get('gst');

    // Check if PAN or GST is selected
    const isPANSelected = panControl?.value && panControl.value.trim() !== '';
    const isGSTSelected = gstControl?.value && gstControl.value.trim() !== '';

    // Check if PAN or GST is valid
    const isPANValid = panControl?.valid;
    const isGSTValid = gstControl?.valid;

    // Return validation errors based on the selection and validity
    //if ((isPANSelected && isGSTSelected) || (!isPANSelected && !isGSTSelected)) {
    if ((!isPANSelected && !isGSTSelected)) {
      return { required: true };
    }

    if (isPANSelected && !isPANValid) {
      return { invalidPAN: true };
    }

    if (isGSTSelected && !isGSTValid) {
      return { invalidGST: true };
    }

    // If both PAN and GST fields are filled, return null to indicate successful validation
    if (isPANSelected && isGSTSelected) {
      return null;
    }

    return null;
  }

  startTimer(): void {
    this.stopTimer(); // Stop the timer if it's already running
    //const totalSeconds = 30; // Total seconds (30 seconds)
    let seconds = this.appGlobals.timer;
    // Update the time initially
    this.updateTimeDisplay(seconds);
    // Start the timer interval
    this.timerInterval = setInterval(() => {
      if (seconds > 0) {
        seconds--;
        this.updateTimeDisplay(seconds);
        this.disableResendButton = seconds < 60; // Enable the button if seconds is less than 30

      } else {
        this.stopTimer(); // Stop the timer when it reaches 0
        this.disableResendButton = false; // Enable the button after the timer reaches 0
      }
    }, 1000);
  }

  updateTimeDisplay(seconds: number): void {
    const remainingSeconds = seconds % 60;
    this.time = `00:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  /** 
  * The moveFocus function is responsible for moving the focus to the next input element when the current input reaches its maximum length.
  * @author Darshan Malani 10:45 AM 6/30/2023
  * @param 
  */
  moveFocus(currentInput: HTMLInputElement, nextInput: HTMLInputElement, previousInput: HTMLInputElement): void {
    if (currentInput.value && currentInput.value.length === currentInput.maxLength) {
      nextInput.focus();
    } else {
      previousInput.focus();
    }
  }

  isRequirementMet(requirement: RegExp): boolean {
    const passwordControl = this.signUpForm.get('password');
    const pattern = new RegExp(requirement);

    return passwordControl.value && pattern.test(passwordControl.value);
  }

  getMaskedEmail(): string {
    const [username, domain] = this.user.email.split('@');
    const maskedUsername = username.substring(0, 4) + username.substring(4).replace(/./g, '*');
    const maskedDomain = domain.substring(0, 2).replace(/./g, '*') + domain.substring(2);

    return maskedUsername + '@' + maskedDomain;
  }

  getMaskedMobileNumber(mobile: string): string {
    return mobile.substring(0, 2) + mobile.substring(2, mobile.length - 2).replace(/./g, '*') + mobile.substring(mobile.length - 2);
  }

  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible; // Toggle the visibility flag
    this.passwordInput.nativeElement.focus(); // Set focus on the password input field
  }

  /** 
  * The fetchtypeOfBusiness function is responsible for fetching the type of business data 
  * @author Darshan Malani 10:45 AM 6/30/2023
  * @param 
  */
  fetchtypeOfBusiness(token: string) {
    this.authService.getDomains(token).subscribe(
      res => {
        this.typeOfBussinessData = res;
        this.companyDetailsForm.get('domain')?.valueChanges.subscribe((selectedOptionId: string) => {
          const othersOption = this.typeOfBussinessData.find(businessType => businessType.name === 'Others');
          this.isOtherSelected = selectedOptionId === (othersOption ? othersOption.id : '');
          // Clear the value of otherDomain if the selected option is not 'Others'
          if (!this.isOtherSelected) {
            this.companyDetailsForm.get('otherDomain')?.setValue('');
          }
        });
      },
      (error: any) => this.sharedService.errorAlert(error)
    );
  }

  onlyNumericValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (value && !/^\d+$/.test(value)) {
      return { numeric: true };
    }
    return null;
  }

  inputFields = [
    { name: 'count1', errorMessages: { required: 'Required.', numeric: 'Only numeric.' } },
    { name: 'count2', errorMessages: { required: 'Required.', numeric: 'Only numeric.' } },
    { name: 'count3', errorMessages: { required: 'Required.', numeric: 'Only numeric.' } },
    { name: 'count4', errorMessages: { required: 'Required.', numeric: 'Only numeric.' } },
    { name: 'count5', errorMessages: { required: 'Required.', numeric: 'Only numeric.' } },
    { name: 'count6', errorMessages: { required: 'Required.', numeric: 'Only numeric.' } }
  ];

  onInputChange(): void {
    this.disableButtonOnError = false; // Enable the button when input changes
    const hasRequiredError = this.inputFields.some(field => {
      const control = this.verifyf[field.name];
      return control.invalid && control.touched && control.errors.required;
    });
    this.errorMessage = hasRequiredError ? 'Required.' : '';
  }

  onGSTAdd() {
    this.companyf.gst.setValue(this.companyf.gst.value?.toUpperCase());
    if (this.companyf.gst.valid) {
      this.companyf.pan.setValue(this.companyf.gst.value?.substring(2, 12)?.toUpperCase());
      // this.onPANChange();
    }
  }

  onPANChange() {
    this.companyf.pan.setValue(this.companyf.pan.value?.toUpperCase());
    if (this.companyf.pan.valid && this.companyf.gst.value && this.companyf.gst.valid) {
      this.companyf.pan.markAsTouched();
      this.companyf.pan.markAsDirty();
      // ! Commented by JIGAR MEHTA //
      // ! Comment code due to mastersindia api limit exceeded we can uncomment this code in future if subscription has been renewed. //
      // const pan = this.companyf.pan.value;
      // this.authService.getPANDetail(pan, this.token).subscribe((GSTNumbers: any) => {
      //   console.log(this.companyf.gst.value, GSTNumbers);
      //   if (GSTNumbers?.length) {
      //     if (!GSTNumbers.some((GST) => GST === this.companyf.gst.value)) {
      //       this.companyf.pan.setErrors({ PANNotMatching: true });
      //     } else {
      //       this.companyf.pan.setErrors(null);
      //     }
      //   } else {
      //     this.companyf.pan.setErrors({ PANNotMatching: true });
      //   }

      // }, (error) => {
      //   console.log(error);
      //   this.sharedService.errorAlert(error);
      // });

    }

  }
}